import React, { useState, useEffect, useContext } from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import { Tab, TabProps, Dimmer, Loader, Button } from 'semantic-ui-react';
import queryString from 'query-string';

import SignUpComponent from '../../components/signUpComponent';
import LoginComponent from '../../components/loginComponent';
import * as Authentication from '../../../services/Authentication';
import * as Database from '../../../services/db';
import * as User from '../../../services/User';

import './style.less';
import ConnectedResident from '../../components/connectedResident';
import UserContext from '../../../contexts/UserContext';
import RedirectContext from '../../../contexts/RedirectContext';
import config from '../../../config';
import SignUpForNeighbors from '../../../neighbors/components/SignUpComponent/signUpForNeighbors';
import AppUrls from '../../../contexts/AppUrls';
import { communityLogoPath, neighborsLogoPath } from '../../../utils/logoPaths';
import { fetchNeighborsIdentity, fetchPublicResidentialFacilities, ResidentialFacilityListItem } from '../../../neighbors/services/Identity';
import { GoogleLoginButton, FacebookLoginButton, AppleLoginButton, AmazonLoginButton } from 'react-social-login-buttons';
import { getMembersOrigin, getNeighborsOrigin } from '../../../utils/platformOrigins';
import { resolveGuestLandingPath } from '../../../neighbors/utils/guestAccessRouting';

const LogIn = (props) => {
    const queryParams = queryString.parse(props.location.search);


    const haveLoggedInBefore = localStorage.getItem('haveLoggedInBefore');

    const [firstName, setFirstName] = useState<string>('');
    const [lastName, setLastName] = useState<string>('');
    const [code, setCode] = useState<string>('');

    const [activeIndex, setActiveIndex] = useState<number>(queryParams.signup ? 1 : haveLoggedInBefore ? 0 : 1);

    const [disabledButton, setDisabledButton] = useState<boolean>(queryParams.signup ? true : false);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const [signUpError, setSignUpError] = useState<string>('');
    const [logInError, setLogInError] = useState<string>('');
    const [skipCodeVerification, setSkipCodeVerification] = useState<boolean>(false);
    const [contact, setContact] = useState<string>('');
    // members is guest-only and neighbors is resident-only - App.tsx's /login route already
    // bounces any guest-intent traffic (?mode=guest, in-progress guest social login) on neighbors
    // over to members before this component ever mounts, so neither platform needs a toggle here.
    const [loginVariant] = useState<string>(
        process.env.REACT_APP_PLATFORM === 'members'
            ? 'guest'
            : (process.env.REACT_APP_PLATFORM === 'neighbors' ? 'resident' : '')
    );
    const [guestAuthMode, setGuestAuthMode] = useState<string>(queryParams.auth === 'signup' ? 'signup' : 'login');
    const [guestEmail, setGuestEmail] = useState<string>('');
    const [guestPassword, setGuestPassword] = useState<string>('');
    const [guestFirstName, setGuestFirstName] = useState<string>('');
    const [guestLastName, setGuestLastName] = useState<string>('');
    const [guestMessage, setGuestMessage] = useState<string>('');
    const [guestConfirmCode, setGuestConfirmCode] = useState<string>('');
    const [confirmError, setConfirmError] = useState<string>('');
    const [resendingCode, setResendingCode] = useState<boolean>(false);
    const [facilities, setFacilities] = useState<ResidentialFacilityListItem[]>([]);
    const { setUser } = useContext(UserContext);
    const { neighborsAndroidAppUrl, neighborsIosAppUrl } = useContext(AppUrls);
    const toggleRedirect = useContext(RedirectContext);

    const env = process.env.SERVER_ENV || 'development';
    const envConfig = config[env];
    const guestSocialAuthConfigured = Authentication.isGuestSocialAuthConfigured();

    useEffect(() => {
        if (process.env.REACT_APP_PLATFORM !== 'members') return;
        fetchPublicResidentialFacilities()
            .then(setFacilities)
            .catch(() => setFacilities([]));
    }, []);

    // Returns null when the account is a family login: those belong on Neighbors, same rule
    // as the page-load check in App.tsx (custom:accountType === 'relative'). The session is
    // signed out here first so Members does not keep it.
    const buildGuestUserObject = async () => {
        const user = await Authentication.checkIfUserIsLoggedin();
        if (user && user.attributes && user.attributes['custom:accountType'] === 'relative') {
            await Authentication.cognitoSignOut().catch(() => {});
            window.location.href = getNeighborsOrigin();
            return null;
        }
        const identity = await fetchNeighborsIdentity();
        const profile = identity.profile || null;
        return {
            cognitoUser: user,
            profile,
            registrants: {
                list: profile ? [profile] : [],
                activeRegistrantsIndexes: profile ? [0] : [],
            },
            username: profile?.Username || profile?.Email || user?.username,
            authenticated: true,
            userType: 'guest',
            guestAccess: identity.access,
            guestIdentity: identity,
            needsOnboarding: identity.needsOnboarding,
        };
    };

    const redirectGuestAfterLogin = (signedInUser) => {
        if (!signedInUser) return;
        const path = resolveGuestLandingPath(signedInUser.guestAccess, signedInUser.needsOnboarding);
        setUser(signedInUser);
        // Defer navigation to next tick so React commits the setUser state update first
        setTimeout(() => toggleRedirect(path), 0);
    };

    useEffect(() => {
        if (process.env.REACT_APP_PLATFORM !== 'members' || !Authentication.hasGuestSocialLoginInProgress()) return;

        let cancelled = false;
        const recoverGuestSocialLogin = async () => {
            setLoading(true);
            for (let attempt = 0; attempt < 4; attempt += 1) {
                await new Promise(resolve => setTimeout(resolve, 500));
                if (cancelled) return;
                const cognitoUser = await Authentication.checkIfUserIsLoggedin();
                if (!cognitoUser) continue;
                if (cancelled) return;
                const signedInUser = await buildGuestUserObject();
                if (cancelled) return;
                Authentication.clearGuestSocialLogin();
                redirectGuestAfterLogin(signedInUser);
                setLoading(false);
                return;
            }
            Authentication.clearGuestSocialLogin();
            setLoading(false);
        };

        recoverGuestSocialLogin().catch(() => {
            Authentication.clearGuestSocialLogin();
            setLoading(false);
        });

        return () => {
            cancelled = true;
        };
    }, []);

    const handleLoginSubmit = async (
        e: React.MouseEvent<HTMLDivElement, MouseEvent>,
        username: string,
        password: string,
        newSignup: boolean = false,
    ): Promise<void> => {
        e.preventDefault();

        setLoading(true);

        try {
            // Validate if the username (contact) is a valid US phone number
            const numberRegExp = /^\+1\d{10}$/; // Ensure it starts with +1 and is a valid 10-digit US number
            if (!numberRegExp.test(username)) {
                setLogInError('Invalid contact. Please enter a valid US phone number starting with +1.');
                setLoading(false);
                return;
            }
            const user = await Authentication.cognitoSignIn(username, password);
            console.log('username', user);
            const dbUserProfileResponse = await Database.getUserDbProfile();
            const userProfile = dbUserProfileResponse.userProfile;

            const connectedRegistrants = await Database.getRelatedRegistrants();

            const signedInUser = {
                cognitoUser: user,
                profile: userProfile,
                registrants: {
                    list: connectedRegistrants.residents,
                    activeRegistrantsIndexes:
                        connectedRegistrants.residents && connectedRegistrants.residents.length ? [0] : [],
                },
                username: user.username,
                authenticated: true,
            };

            if (!userProfile) throw Error('No user profile');
            if (process.env.REACT_APP_PLATFORM === 'neighbors') {
                const managedResident = signedInUser && signedInUser.registrants && signedInUser.registrants.list && signedInUser.registrants.list.find((registrant) => {
                    return registrant.Facility && (registrant.manager || registrant.isCommunityManager)
                })
                if (managedResident) {  // If the env is neighbors, then set active registrant to the managed resident
                    const indexOfManagedResident = signedInUser.registrants.list.indexOf(managedResident);
                    signedInUser.registrants.activeRegistrantsIndexes = [indexOfManagedResident];
                }
            }
            // setUser and the post-login navigation must land in the SAME render, otherwise
            // there's a tick where the URL is still '/login' but authenticated is already true
            // (or vice versa), which races with the /login route's own declarative
            // authenticated-redirect logic in App.tsx and can bounce to the wrong place.
            unstable_batchedUpdates(() => {
                setUser(signedInUser);
                if (newSignup && connectedRegistrants.residents && connectedRegistrants.residents.length === 0) {
                    toggleRedirect('/message-recipients');
                } else {
                    if (process.env.REACT_APP_PLATFORM === 'community') {
                        toggleRedirect('/send-message');
                    } else if (process.env.REACT_APP_PLATFORM === 'neighbors') {
                        toggleRedirect('/');
                    }
                    else {
                        toggleRedirect('/activities');
                    }
                }
            });
            return;
        } catch (error) {
            console.log("error in handleLoginSubmit", error);
            const userExists = await Authentication.checkIfUserExists(username);
            if (!userExists) {
                setLogInError('User does not exist');
            } else {
              //if there is an errormessage then set it
              if (error.message) setLogInError(error.message);
              else setLogInError('Unable to log in.');
            }
            setLoading(false);
        }
    };

    const handleSignUpSubmit = async (
        e: React.MouseEvent<HTMLDivElement, MouseEvent>,
        contact: string,
        password: string,
    ): Promise<any> => {
        e.preventDefault();

        setLoading(true);

        const signUpParams = { password, firstName, lastName };

        if (code) {
            // @ts-ignore
            signUpParams.token = code;
        }

        // @ts-ignore
        signUpParams.contact = contact;

        try {
            console.log('signup params,', signUpParams);
            await Authentication.cognitoSignUp(signUpParams);

            handleLoginSubmit(e, contact, password, true);
            return;
        } catch (error) {
            setLoading(false);
            setSignUpError('Unable to sign up user.');
        }
    };

    const handleGuestEmailLogin = async (e) => {
        e.preventDefault();
        setLoading(true);
        setLogInError('');
        try {
            await Authentication.guestCognitoSignIn(guestEmail, guestPassword);
            const signedInUser = await buildGuestUserObject();
            redirectGuestAfterLogin(signedInUser);
        } catch (error) {
            if (error && error.code === 'UserNotConfirmedException') {
                setGuestMessage('Your account needs email confirmation. Enter the code we sent you, or resend it below.');
                setGuestAuthMode('confirm');
                setLoading(false);
                return;
            }
            setLogInError(error instanceof Error ? error.message : 'Unable to log in as guest.');
            setLoading(false);
        }
    };

    const handleGuestEmailSignUp = async (e) => {
        e.preventDefault();
        setLoading(true);
        setSignUpError('');
        try {
            await Authentication.guestCognitoSignUp({
                email: guestEmail,
                password: guestPassword,
                firstName: guestFirstName,
                lastName: guestLastName,
            });
            setGuestMessage('Account created. Enter the confirmation code we sent to your email.');
            setGuestAuthMode('confirm');
        } catch (error) {
            setSignUpError(error instanceof Error ? error.message : 'Unable to sign up as guest.');
        } finally {
            setLoading(false);
        }
    };

    const handleGuestConfirmSignUp = async (e) => {
        e.preventDefault();
        setLoading(true);
        setConfirmError('');
        try {
            await Authentication.confirmGuestSignUp(guestEmail, guestConfirmCode);
            setGuestConfirmCode('');
            try {
                // Sign the guest straight in with the credentials they just signed up with,
                // rather than making them re-enter their password after confirming.
                await Authentication.guestCognitoSignIn(guestEmail, guestPassword);
                const signedInUser = await buildGuestUserObject();
                redirectGuestAfterLogin(signedInUser);
                return;
            } catch (signInError) {
                setGuestMessage('Email confirmed. Please sign in.');
                setGuestAuthMode('login');
                setLoading(false);
            }
        } catch (error) {
            setConfirmError(error instanceof Error ? error.message : 'Unable to confirm account.');
            setLoading(false);
        }
    };

    // The guest auth links below are styled as <a> but trigger in-page state changes rather
    // than real navigation, so they need an explicit keyboard handler to stay reachable via Tab/Enter.
    const activateOnEnter = (handler: () => void) => (e: React.KeyboardEvent<HTMLAnchorElement>) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handler();
        }
    };

    const handleResendGuestConfirmationCode = async () => {
        if (resendingCode) return;
        setConfirmError('');
        setResendingCode(true);
        try {
            await Authentication.resendConfirmationCode(guestEmail);
            setGuestMessage('A new confirmation code has been sent to your email.');
        } catch (error) {
            setConfirmError(error instanceof Error ? error.message : 'Unable to resend code.');
        } finally {
            setResendingCode(false);
        }
    };

    const handleGuestSocialLogin = async (provider: string) => {
        setLoading(true);
        try {
            await Authentication.guestFederatedSignIn(provider);
        } catch (error) {
            Authentication.clearGuestSocialLogin();
            setLogInError(error instanceof Error ? error.message : 'Unable to start social login.');
            setLoading(false);
        }
    };

    const toggleLoginOrSignup = () => {
        if (loginOrSignup === 'login') {
            setLoginOrSignup('signup')
        } else {
            setLoginOrSignup('login')
        }
    }

    const [loginOrSignup, setLoginOrSignup] = useState('login')
    const CommunityNeighborsLoginOrSignUpComponent = () => {
        if (!loading) {
            if (loginOrSignup === 'login') {
                return <LoginComponent handleLoginSubmit={handleLoginSubmit} error={logInError} locationState={props.location.state}/>
            } else if (loginOrSignup === 'signup') {
                return (
                    <div>
                        {process.env.REACT_APP_PLATFORM === 'neighbors' ? <SignUpForNeighbors
                            firstName={firstName}
                            lastName={lastName}
                            contact={contact}
                            setFirstName={setFirstName}
                            setLastName={setLastName}
                            code={code}
                            setCode={setCode}
                            handleSignUpSubmit={handleSignUpSubmit}
                            error={signUpError}
                            disabledButton={disabledButton}
                            setLoading={setLoading}
                            skipCodeVerification={skipCodeVerification}
                            setSkipCodeVerification={setSkipCodeVerification}
                            setContact={setContact}
                        /> :
                            <SignUpComponent
                                firstName={firstName}
                                lastName={lastName}
                                setFirstName={setFirstName}
                                setLastName={setLastName}
                                setCode={setCode}
                                handleSignUpSubmit={handleSignUpSubmit}
                                error={signUpError}
                                disabledButton={disabledButton}
                            />
                        }
                        <div style={{ textAlign: 'center' }}>
                            <br />
                            <a className="text-center" onClick={toggleLoginOrSignup} style={{ cursor: 'pointer' }}>Back to Login</a>
                        </div>
                    </div>
                )
            }
        }
    }

    const CommunitySignupButton = () => {
        if (loginOrSignup === 'login') {
            return (
                <div className="signup-container">
                    <Button className="signup-button" onClick={toggleLoginOrSignup} role="link" color="teal" basic>
                        Sign Up
                    </Button>
                </div>
            )
        } else if (loginOrSignup === 'signup') {
            return ''
        }
    }

    const CommunityAppLinksComponent = () => {
        if (!loading) {
            return (
                <div className="app-links">
                    <a
                        href="https://play.google.com/store/apps/details?id=com.speak2software.community"
                        className="play-store"
                    >
                        <img
                            alt="Get it on Google Play"
                            src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png"
                        />
                    </a>
                    <a href="https://apps.apple.com/ua/app/speak2-community/id1596060585" className="app-store">
                        <img src="/Download_on_the_App_Store_Badge_US-UK_RGB_blk_092917.svg" />
                    </a>
                </div>
            )
        }
    }

    const getFacilityInitials = (name: string) => {
        const parts = (name || '').trim().split(/\s+/).filter(Boolean);
        if (parts.length === 0) return '?';
        if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    };

    const guestSocialButtonStyle = {
        background: '#fff',
        color: '#1B2533',
        border: '1.5px solid #E2E9F3',
        borderRadius: 13,
        boxShadow: 'none',
        fontFamily: 'inherit',
        fontSize: '14.5px',
        fontWeight: 600,
        padding: '13px 14px',
    };
    const guestSocialButtonActiveStyle = { background: '#EEF4FD', borderColor: '#DCE9FB' };

    const GuestAuthComponent = () => (
        <div className="guest-login-page">
            <main className="guest-login-shell">
                <aside className="guest-login-info">
                    <div className="guest-login-logo">
                        <img
                            src={`${process.env.PUBLIC_URL}${neighborsLogoPath}`}
                            alt="Speak2 Family"
                            className="guest-login-logo-img"
                        />
                    </div>

                    <div className="guest-login-block">
                        <h3>Available communities</h3>
                        <div className="guest-login-communities">
                            {facilities.length === 0 ? (
                                <p className="guest-login-empty">No residential facilities are available right now.</p>
                            ) : (
                                facilities.map((facility, index) => {
                                    const name = facility.communityName || facility.facilityName;
                                    return (
                                        <div className="guest-login-community" key={facility.facilityId}>
                                            <span className={`guest-login-avatar guest-login-c${(index % 3) + 1}`}>
                                                {getFacilityInitials(name)}
                                            </span>
                                            <span className="guest-login-meta">
                                                <b>{name}</b>
                                                <span>{[facility.city, facility.state].filter(Boolean).join(', ')}</span>
                                            </span>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </div>

                    <div className="guest-login-block">
                        <h3>How it works</h3>
                        <div className="guest-login-steps">
                            <div className="guest-login-step">
                                <span className="guest-login-num">1</span>
                                <p>Find your community<span>Pick your community from the list.</span></p>
                            </div>
                            <div className="guest-login-step">
                                <span className="guest-login-num">2</span>
                                <p>Sign in or create an account<span>Use email or a service you already have.</span></p>
                            </div>
                            <div className="guest-login-step">
                                <span className="guest-login-num">3</span>
                                <p>Stay connected<span>See updates, content and more from your community.</span></p>
                            </div>
                        </div>
                    </div>
                </aside>

                <section className="guest-login-form-panel">
                    <div className="guest-login-eyebrow">{guestAuthMode === 'login' ? 'Sign in' : guestAuthMode === 'confirm' ? 'Confirm email' : 'Sign up'}</div>
                    <h2>{guestAuthMode === 'login' ? 'Welcome back' : guestAuthMode === 'confirm' ? 'Check your email' : 'Create your account'}</h2>
                    <p className="guest-login-sub">
                        {guestAuthMode === 'confirm'
                            ? `Enter the code we sent to ${guestEmail || 'your email'}.`
                            : 'Choose how you’d like to continue.'}
                    </p>

                    {guestAuthMode !== 'confirm' && (guestSocialAuthConfigured ? (
                        <div className="guest-login-social">
                            <GoogleLoginButton
                                onClick={() => handleGuestSocialLogin('Google')}
                                style={guestSocialButtonStyle}
                                activeStyle={guestSocialButtonActiveStyle}
                                align="center"
                                text="Google"
                                iconSize="19px"
                            />
                            <AppleLoginButton
                                onClick={() => handleGuestSocialLogin('SignInWithApple')}
                                style={guestSocialButtonStyle}
                                activeStyle={guestSocialButtonActiveStyle}
                                align="center"
                                text="Apple"
                                iconSize="19px"
                            />
                            <FacebookLoginButton
                                onClick={() => handleGuestSocialLogin('Facebook')}
                                style={guestSocialButtonStyle}
                                activeStyle={guestSocialButtonActiveStyle}
                                align="center"
                                text="Facebook"
                                iconSize="19px"
                                iconColor="#1877F2"
                            />
                            <AmazonLoginButton
                                onClick={() => handleGuestSocialLogin('LoginWithAmazon')}
                                style={guestSocialButtonStyle}
                                activeStyle={guestSocialButtonActiveStyle}
                                align="center"
                                text="Amazon"
                                iconSize="19px"
                                iconColor="#FF9900"
                            />
                        </div>
                    ) : (
                        <div className="guest-login-notice">
                            Social login is not configured for this environment. Use email {guestAuthMode === 'login' ? 'sign in' : 'sign up'} below.
                        </div>
                    ))}

                    {guestAuthMode !== 'confirm' && (
                        <div className="guest-login-divider">or {guestAuthMode === 'login' ? 'sign in' : 'sign up'} with email</div>
                    )}

                    {guestMessage && <div className="guest-login-info-message">{guestMessage}</div>}

                    {guestAuthMode === 'login' ? (
                        <form className="guest-login-form" onSubmit={handleGuestEmailLogin}>
                            <div className="guest-login-field">
                                <label htmlFor="guest-login-email">Email address</label>
                                <input
                                    id="guest-login-email"
                                    type="email"
                                    placeholder="you@email.com"
                                    autoComplete="email"
                                    value={guestEmail}
                                    onChange={(e) => setGuestEmail(e.target.value)}
                                />
                            </div>
                            <div className="guest-login-field">
                                <label htmlFor="guest-login-password">Password</label>
                                <input
                                    id="guest-login-password"
                                    type="password"
                                    placeholder="••••••••"
                                    autoComplete="current-password"
                                    value={guestPassword}
                                    onChange={(e) => setGuestPassword(e.target.value)}
                                />
                            </div>
                            <div className="guest-login-row-between">
                                <a
                                    href="/forgot-password?mode=guest"
                                    onClick={(e) => { e.preventDefault(); props.history.push('/forgot-password?mode=guest'); }}
                                >
                                    Forgot password?
                                </a>
                            </div>
                            {logInError && <div className="guest-login-error">{logInError}</div>}
                            <button className="guest-login-btn-primary" type="submit">Sign in</button>
                            <p className="guest-login-switch">
                                New to Speak2 Family? <a
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => setGuestAuthMode('signup')}
                                    onKeyDown={activateOnEnter(() => setGuestAuthMode('signup'))}
                                >Create an account</a>
                            </p>
                        </form>
                    ) : guestAuthMode === 'confirm' ? (
                        <form className="guest-login-form" onSubmit={handleGuestConfirmSignUp}>
                            <div className="guest-login-field">
                                <label htmlFor="guest-confirm-code">Confirmation code</label>
                                <input
                                    id="guest-confirm-code"
                                    placeholder="123456"
                                    autoComplete="one-time-code"
                                    value={guestConfirmCode}
                                    onChange={(e) => setGuestConfirmCode(e.target.value)}
                                />
                            </div>
                            {confirmError && <div className="guest-login-error">{confirmError}</div>}
                            <button className="guest-login-btn-primary" type="submit">Confirm account</button>
                            <p className="guest-login-switch">
                                Didn&apos;t get a code? <a
                                    role="button"
                                    aria-disabled={resendingCode}
                                    tabIndex={0}
                                    onClick={resendingCode ? undefined : handleResendGuestConfirmationCode}
                                    onKeyDown={activateOnEnter(handleResendGuestConfirmationCode)}
                                >{resendingCode ? 'Sending…' : 'Resend code'}</a>
                            </p>
                            <p className="guest-login-switch">
                                <a
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => setGuestAuthMode('login')}
                                    onKeyDown={activateOnEnter(() => setGuestAuthMode('login'))}
                                >Back to sign in</a>
                            </p>
                        </form>
                    ) : (
                        <form className="guest-login-form" onSubmit={handleGuestEmailSignUp}>
                            <div className="guest-login-field-row">
                                <div className="guest-login-field">
                                    <label htmlFor="guest-signup-first-name">First name</label>
                                    <input
                                        id="guest-signup-first-name"
                                        value={guestFirstName}
                                        onChange={(e) => setGuestFirstName(e.target.value)}
                                    />
                                </div>
                                <div className="guest-login-field">
                                    <label htmlFor="guest-signup-last-name">Last name</label>
                                    <input
                                        id="guest-signup-last-name"
                                        value={guestLastName}
                                        onChange={(e) => setGuestLastName(e.target.value)}
                                    />
                                </div>
                            </div>
                            <div className="guest-login-field">
                                <label htmlFor="guest-signup-email">Email address</label>
                                <input
                                    id="guest-signup-email"
                                    type="email"
                                    placeholder="you@email.com"
                                    autoComplete="email"
                                    value={guestEmail}
                                    onChange={(e) => setGuestEmail(e.target.value)}
                                />
                            </div>
                            <div className="guest-login-field">
                                <label htmlFor="guest-signup-password">Password</label>
                                <input
                                    id="guest-signup-password"
                                    type="password"
                                    placeholder="••••••••"
                                    autoComplete="new-password"
                                    value={guestPassword}
                                    onChange={(e) => setGuestPassword(e.target.value)}
                                />
                            </div>
                            {signUpError && <div className="guest-login-error">{signUpError}</div>}
                            <button className="guest-login-btn-primary" type="submit">Create account</button>
                            <p className="guest-login-switch">
                                Already have an account? <a
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => setGuestAuthMode('login')}
                                    onKeyDown={activateOnEnter(() => setGuestAuthMode('login'))}
                                >Sign in</a>
                            </p>
                        </form>
                    )}

                    <p className="guest-login-back">
                        <a
                            href="/welcome"
                            onClick={(e) => { e.preventDefault(); props.history.push('/welcome'); }}
                        >
                            ← Back to welcome
                        </a>
                    </p>
                    {/* This component only renders on the members platform (loginVariant is fixed to 'guest' there). */}
                    <p className="guest-login-back">
                        <a href={getNeighborsOrigin()}>Resident or staff? Go to neighbors.speak2family.com</a>
                    </p>
                </section>
            </main>
        </div>
    );

    return (
        <div className={`login-page${loginVariant === 'guest' ? ' login-page--guest' : ''}`}>
        <div className={`login-wrapper${loginVariant === 'guest' ? ' login-wrapper--guest' : ''}`}>
            <Dimmer active={loading} inverted>
                <Loader active={loading} />
            </Dimmer>
            {process.env.REACT_APP_PLATFORM === 'community'
                && <img src={`${process.env.PUBLIC_URL}/logo-speak2-community.svg`} alt="Speak 2 Community Logo" className="logo" />}
            {process.env.REACT_APP_PLATFORM === 'neighbors' && loginVariant !== 'guest' && <img src={`${process.env.PUBLIC_URL}${neighborsLogoPath}`} alt="Speak 2 Neighbors Logo" className="logo" />}
            {(process.env.REACT_APP_PLATFORM !== 'community' && process.env.REACT_APP_PLATFORM !== 'neighbors' && process.env.REACT_APP_PLATFORM !== 'members') && <img src={`${process.env.PUBLIC_URL}${communityLogoPath}`} alt="Speak 2 Family Logo" className="logo" />
            }

                {(process.env.REACT_APP_PLATFORM === 'neighbors' || process.env.REACT_APP_PLATFORM === 'members') && loginVariant === 'guest'
            ?   GuestAuthComponent()
            : process.env.REACT_APP_PLATFORM === 'community' || process.env.REACT_APP_PLATFORM === 'neighbors'
            ?   CommunityNeighborsLoginOrSignUpComponent()
                        : <LoginComponent handleLoginSubmit={handleLoginSubmit} error={logInError} locationState={props.location.state} />
            }

                {process.env.REACT_APP_PLATFORM === 'community' || process.env.REACT_APP_PLATFORM === 'neighbors'
                    ? (process.env.REACT_APP_PLATFORM === 'neighbors' && loginVariant !== 'resident' ? null : CommunitySignupButton())
                    : process.env.REACT_APP_PLATFORM === 'members'
                    ? null
                    : <div className="signup-container">
                        If you don't have an account
                        <Button className="signup-button" href={envConfig.signUpUrl} role="link" color="teal" basic>
                            Sign Up
                        </Button>
                    </div>

            }

                {process.env.REACT_APP_PLATFORM === 'neighbors' && loginVariant === 'resident' && (
                    <div style={{ textAlign: 'center', marginTop: '16px', padding: '10px 0', borderTop: '1px solid #e8e8e8' }}>
                        <a href={getMembersOrigin()} style={{ cursor: 'pointer', color: '#2185d0', fontWeight: 600, fontSize: '1em' }}>
                            Click here for guest access
                        </a>
                    </div>
                )}

            <h4 className="text-center">The app is available for download</h4>
            {process.env.REACT_APP_PLATFORM === 'community'
                ? CommunityAppLinksComponent()

                : <div className="app-links">
                    <a
                        href={(process.env.REACT_APP_PLATFORM === "neighbors" || process.env.REACT_APP_PLATFORM === "members") ? neighborsAndroidAppUrl : "https://play.google.com/store/apps/details?id=com.speak2software.family&pcampaignid=pcampaignidMKT-Other-global-all-co-prtnr-py-PartBadge-Mar2515-1"}
                        className="play-store"
                        target="_blank"
                    >
                        <img
                            alt="Get it on Google Play"
                            src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png"
                        />
                    </a>
                    <a href={(process.env.REACT_APP_PLATFORM === "neighbors" || process.env.REACT_APP_PLATFORM === "members") ? neighborsIosAppUrl : "https://apps.apple.com/us/app/speak2-family/id1503580779"} className="app-store" target="_blank">
                        <img src="/Download_on_the_App_Store_Badge_US-UK_RGB_blk_092917.svg" />
                    </a>
                </div>
            }
        </div>
        </div>
    );
};

function getDecodedUrlSegments(params) {
    try {
        let {
            t: token,
            r: registrantId,
            c: receivedContact,
            f: registrantFirstName,
            l: registrantLastName,
            i: image,
        } = params;
        return {
            token: atob(token || ''),
            registrantId: atob(registrantId || ''),
            receivedContact: atob(receivedContact || ''),
            registrantFirstName: atob(registrantFirstName || ''),
            registrantLastName: atob(registrantLastName || ''),
            // image: 'https://' + atob(image || ''),
            image: image ? 'https://' + atob(image) : undefined,
        };
    } catch (error) {
        return {
            token: '',
            registrantId: '',
            receivedContact: '',
            registrantFirstName: '',
            registrantLastName: '',
            image: '',
        };
    }
}

export default LogIn;
